const SORT_VALUES = ["newest", "oldest", "az", "za"];
const DEFAULT_SORT = "newest";

const LAYOUT_VALUES = ["list", "column"];
const DEFAULT_LAYOUT = "list";

/**
 * Reads WWG Store filter/sort/layout state from a query string, falling
 * back to defaults for missing or unrecognized values. Category/subCategory
 * are returned as-is (validity against the loaded category tree is checked
 * separately by reconcileCategoryParams once categories have loaded).
 */
export function parseStoreFilterParams(search) {
  const params = new URLSearchParams(search || "");
  const rawSort = params.get("sort");
  const rawLayout = params.get("layout");

  return {
    sort: SORT_VALUES.includes(rawSort) ? rawSort : DEFAULT_SORT,
    layout: LAYOUT_VALUES.includes(rawLayout) ? rawLayout : DEFAULT_LAYOUT,
    category: params.get("category") || "",
    subCategory: params.get("sub") || "",
    searchTerm: params.get("q") || "",
  };
}

/**
 * Serializes WWG Store filter state back into a query string, omitting
 * default/empty values so the URL stays clean (e.g. no `?sort=newest`).
 */
export function buildStoreFilterSearch({
  sort,
  layout,
  category,
  subCategory,
  searchTerm,
}) {
  const params = new URLSearchParams();

  if (sort && sort !== DEFAULT_SORT) params.set("sort", sort);
  if (layout && layout !== DEFAULT_LAYOUT) params.set("layout", layout);
  if (category) params.set("category", category);
  if (subCategory) params.set("sub", subCategory);
  if (searchTerm) params.set("q", searchTerm);

  return params.toString();
}

function collectValidSubCategoryValues(categoryValue) {
  if (!categoryValue) return new Set();
  if (Array.isArray(categoryValue)) return new Set(categoryValue);

  if (typeof categoryValue === "object") {
    const values = new Set(Object.keys(categoryValue));
    Object.values(categoryValue).forEach((child) => {
      // Middle-level entries are { rowNumber, items: [{ title, rowNumber }] }
      // (see buildCategoriesFromAPI) rather than plain arrays, so pull the
      // leaf titles out of `items`.
      const leafItems = Array.isArray(child) ? child : child?.items || [];
      leafItems.forEach((leaf) => {
        values.add(typeof leaf === "string" ? leaf : leaf?.title);
      });
    });
    return values;
  }

  return new Set();
}

/**
 * Validates a category/subCategory pair against the category tree built
 * from the API response, dropping values that no longer exist (e.g. a
 * category was renamed or removed after a filtered URL was bookmarked).
 * Favorites has no subcategories in the UI, so any subCategory alongside
 * it is always dropped. Returns `changed: true` when the caller should
 * rewrite state/URL to reflect the correction.
 */
export function reconcileCategoryParams(
  { category, subCategory },
  categoryTree
) {
  if (!category) {
    return { category: "", subCategory: "", changed: Boolean(subCategory) };
  }

  const categoryValue = categoryTree?.[category];
  const categoryIsValid = categoryValue !== undefined;

  if (!categoryIsValid) {
    return { category: "", subCategory: "", changed: true };
  }

  if (category === "Favorites") {
    return { category, subCategory: "", changed: Boolean(subCategory) };
  }

  if (!subCategory) {
    return { category, subCategory: "", changed: false };
  }

  const validSubCategoryValues = collectValidSubCategoryValues(categoryValue);
  if (!validSubCategoryValues.has(subCategory)) {
    return { category, subCategory: "", changed: true };
  }

  return { category, subCategory, changed: false };
}
